import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap, BookOpen, Heart, Users,
  Target, BarChart2, Trophy, Star, Zap, Shield,
  MapPin, Award
} from "lucide-react";
import "./Testimonials.scss";

import student1 from "../assets/testimonials/student-1_11zon.webp";
import student2 from "../assets/testimonials/student-2_11zon.webp";
import teacher1 from "../assets/testimonials/teacher-1_11zon.webp";
import teacher2 from "../assets/testimonials/teacher-2_11zon.webp";
import parent1  from "../assets/testimonials/parent-1_11zon.webp";
import parent2  from "../assets/testimonials/parent-2_11zon.webp";

const DATA = {
  students: [
    {
      id: 1, name: "Arpit Sikarwar", photo: student1,
      detail: "Class 12 Student", location: "Uttar Pradesh",
      text: "Cograd has been instrumental in shaping my career path. Their personalized guidance and industry insights helped me secure a prestigious internship, setting me on the right track towards my professional goals.",
      tags: [
        { icon: Target,    title: "Personalized Guidance" },
        { icon: BarChart2, title: "Industry Insights"     },
        { icon: Trophy,    title: "Career Opportunities"  },
      ],
    },
    {
      id: 2, name: "Khushbu", photo: student2,
      detail: "Class 11 Student", location: "Uttar Pradesh",
      text: "My experience with Cograd has been truly transformative. Their dedicated mentors and practical curriculum equipped me with the skills and confidence to excel in my chosen field and beyond.",
      tags: [
        { icon: Star,   title: "Expert Mentorship"   },
        { icon: Zap,    title: "Practical Skills"    },
        { icon: Shield, title: "Confidence Building" },
      ],
    },
  ],
  teachers: [
    {
      id: 3, name: "Saurabh Singh", photo: teacher2,
      detail: "Science Teacher", location: "Uttar Pradesh",
      text: "Cograd's innovative approach to education has truly rejuvenated my teaching journey. Their emphasis on interactive learning tools and continuous professional development has made me a far more effective educator.",
      tags: [
        { icon: Zap,      title: "Interactive Tools"   },
        { icon: Award,    title: "Professional Growth" },
        { icon: BookOpen, title: "Modern Methods"      },
      ],
    },
    {
      id: 4, name: "Seetu Yadav", photo: teacher1,
      detail: "Mathematics Teacher", location: "Uttar Pradesh",
      text: "Cograd's support was invaluable in my transition to teaching abroad. Their global network and resources helped me navigate the complexities seamlessly — I'm thriving in my new role.",
      tags: [
        { icon: Users,  title: "Global Network"    },
        { icon: Target, title: "Career Transition" },
        { icon: Trophy, title: "New Opportunities" },
      ],
    },
  ],
  parents: [
    {
      id: 5, name: "Seelendra Singh", photo: parent1,
      detail: "Parent of Class 10 Student", location: "Uttar Pradesh",
      text: "Enrolling my child in Cograd's advanced courses was a game-changer. The personalized attention and hands-on learning approach significantly boosted my child's academic performance and confidence.",
      tags: [
        { icon: Star,      title: "Personalized Attention" },
        { icon: BookOpen,  title: "Hands-on Learning"     },
        { icon: BarChart2, title: "Academic Growth"        },
      ],
    },
    {
      id: 6, name: "Saurabh Yadav", photo: parent2,
      detail: "Parent of Class 9 Student", location: "Uttar Pradesh",
      text: "The level of care Cograd puts into each student is remarkable. My child's progress over just one semester has exceeded every expectation we had as parents.",
      tags: [
        { icon: Heart,     title: "Expert Care"    },
        { icon: BarChart2, title: "Rapid Progress" },
        { icon: Shield,    title: "Parent Trust"   },
      ],
    },
  ],
};

const TABS = [
  { key: "students", label: "Students", icon: GraduationCap },
  { key: "teachers", label: "Teachers", icon: BookOpen },
  { key: "parents",  label: "Parents",  icon: Heart },
];

function StarRow() {
  return (
    <div className="tm__stars">
      {[1,2,3,4,5].map(i => (
        <Star key={i} size={13} fill="#fbbf24" color="#fbbf24" />
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [category, setCategory] = useState("students");
  const items = DATA[category];

  return (
    <section className="tm-wrap">
      <div className="tm-inner">

        {/* ── Header ── */}
        <motion.div
          className="tm__header"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <div className="tm__chip"><Users size={12} strokeWidth={2.5} /> Community Stories</div>
          <h2 className="tm__h2">What Our <span>Community</span> Says</h2>
          <p className="tm__sub">
            Real stories from real people whose lives have been
            impacted through our programs and initiatives.
          </p>
          <div className="tm__rating-row">
            <StarRow />
            <span className="tm__rating-txt">4.9/5 from <strong>300K+</strong> learners across India</span>
          </div>
        </motion.div>

        {/* ── Tabs ── */}
        <div className="tm__tabs">
          {TABS.map((tab) => {
            const TIcon = tab.icon;
            return (
              <button
                key={tab.key}
                className={`tm__tab${category === tab.key ? " tm__tab--active" : ""}`}
                onClick={() => setCategory(tab.key)}
              >
                <TIcon size={15} strokeWidth={2} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── Cards ── */}
        <AnimatePresence mode="wait">
          <motion.div
            key={category}
            className="tm__grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            {items.map((t, i) => {
              return (
                <motion.div
                  key={t.id}
                  className={`tm__card${i === 0 ? " tm__card--featured" : ""}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: i * 0.07, ease: "easeOut" }}
                >
                  {/* Decorative quote */}
                  <span className="tm__deco">&ldquo;</span>

                  {/* Quote text */}
                  <p className="tm__text">&ldquo;{t.text}&rdquo;</p>

                  {/* Stars */}
                  <StarRow />

                  {/* Divider */}
                  <div className="tm__rule" />

                  {/* Person */}
                  <div className="tm__person">
                    <img src={t.photo} alt={t.name} className="tm__avatar" loading="lazy" />
                    <div className="tm__person-info">
                      <span className="tm__name">{t.name}</span>
                      <span className="tm__role">
                        <GraduationCap size={11} strokeWidth={2} />
                        {t.detail}
                        <span className="tm__dot">·</span>
                        <MapPin size={11} strokeWidth={2} />
                        {t.location}
                      </span>
                    </div>
                  </div>


                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>

      </div>
    </section>
  );
}
